/**
 * Security utilities for Lasmari Chauffeurs
 * Provides client-side security measures and secure data handling
 */

// Simple encryption for sensitive data in localStorage
const ENCRYPTION_KEY = 'LamariCars2024!@#SecureKey';

// Simple XOR encryption (for basic obfuscation - not cryptographically secure)
export const encryptData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    let encrypted = '';
    
    for (let i = 0; i < jsonString.length; i++) {
      const charCode = jsonString.charCodeAt(i);
      const keyCode = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      encrypted += String.fromCharCode(charCode ^ keyCode);
    }
    
    return btoa(encrypted); // Base64 encode
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

// Decrypt data
export const decryptData = (encryptedData) => {
  try {
    const encrypted = atob(encryptedData); // Base64 decode
    let decrypted = '';
    
    for (let i = 0; i < encrypted.length; i++) {
      const charCode = encrypted.charCodeAt(i);
      const keyCode = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      decrypted += String.fromCharCode(charCode ^ keyCode);
    }
    
    return JSON.parse(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

// Secure localStorage wrapper
export const secureStorage = {
  setItem: (key, value) => {
    try {
      const encrypted = encryptData(value);
      if (encrypted) {
        localStorage.setItem(`sec_${key}`, encrypted);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Secure storage set error:', error);
      return false;
    }
  },
  
  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(`sec_${key}`);
      if (encrypted) {
        return decryptData(encrypted);
      }
      return null;
    } catch (error) {
      console.error('Secure storage get error:', error);
      return null;
    }
  },
  
  removeItem: (key) => {
    try {
      localStorage.removeItem(`sec_${key}`);
      return true;
    } catch (error) {
      console.error('Secure storage remove error:', error);
      return false;
    }
  },
  
  clear: () => {
    try {
      // Only clear items with our prefix
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('sec_')) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Secure storage clear error:', error);
      return false;
    }
  }
};

// Security headers checker
export const checkSecurityHeaders = async () => {
  try {
    const response = await fetch(window.location.origin, {
      method: 'HEAD'
    });
    
    const headers = {};
    for (const [key, value] of response.headers.entries()) {
      headers[key.toLowerCase()] = value;
    }
    
    const securityChecks = {
      'content-security-policy': headers['content-security-policy'] ? 'present' : 'missing',
      'x-frame-options': headers['x-frame-options'] ? 'present' : 'missing',
      'x-content-type-options': headers['x-content-type-options'] ? 'present' : 'missing',
      'referrer-policy': headers['referrer-policy'] ? 'present' : 'missing',
      'permissions-policy': headers['permissions-policy'] ? 'present' : 'missing'
    };
    
    return securityChecks;
  } catch (error) {
    console.error('Security headers check failed:', error);
    return null;
  }
};

// Session management
export const sessionManager = {
  // Create a session token
  createSession: (userData) => {
    const sessionData = {
      ...userData,
      timestamp: Date.now(),
      sessionId: generateSessionId()
    };
    
    secureStorage.setItem('user_session', sessionData);
    return sessionData;
  },
  
  // Get current session
  getSession: () => {
    const session = secureStorage.getItem('user_session');
    if (session && Date.now() - session.timestamp < 24 * 60 * 60 * 1000) { // 24 hours
      return session;
    }
    return null;
  },
  
  // Update session
  updateSession: (updates) => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = {
        ...currentSession,
        ...updates,
        timestamp: Date.now()
      };
      secureStorage.setItem('user_session', updatedSession);
      return updatedSession;
    }
    return null;
  },
  
  // Destroy session
  destroySession: () => {
    secureStorage.removeItem('user_session');
  }
};

// Generate secure session ID
const generateSessionId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result + Date.now().toString(36);
};

// Input sanitization for display
export const sanitizeForDisplay = (str) => {
  if (typeof str !== 'string') return '';
  
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// CSRF token management
export const csrfManager = {
  generateToken: () => {
    const token = Math.random().toString(36).substring(2, 15) + 
                  Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('csrf_token', token);
    return token;
  },
  
  getToken: () => {
    return sessionStorage.getItem('csrf_token');
  },
  
  validateToken: (token) => {
    const storedToken = sessionStorage.getItem('csrf_token');
    return storedToken && storedToken === token;
  },
  
  clearToken: () => {
    sessionStorage.removeItem('csrf_token');
  }
};

// Content Security Policy helper
export const setupCSP = () => {
  // Create a meta tag for CSP if not already present
  if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    
    // Detect development vs production environment
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isLocalNetwork = hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.');
    const isDevelopment = isLocalhost || isLocalNetwork || hostname.includes('.local');
    
    // Configure connect-src based on environment
    let connectSrc;
    if (isDevelopment) {
      // Development: Allow all connections
      connectSrc = "'self' *";
    } else {
      // Production: Restrict to necessary domains. Backend is on Hetzner;
      // Supabase fully retired (auth + DB now on api.lasmarichauffeurs.com).
      connectSrc = "'self' api.lasmarichauffeurs.com maps.googleapis.com *.googleapis.com";
    }
    
    cspMeta.content = `default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' maps.googleapis.com *.googleapis.com; style-src 'self' 'unsafe-inline' fonts.googleapis.com *.googleapis.com; font-src 'self' fonts.gstatic.com *.googleapis.com; img-src 'self' data: maps.googleapis.com *.googleapis.com *.gstatic.com; connect-src ${connectSrc};`;
    document.head.appendChild(cspMeta);
    
    console.log('🔒 CSP configured for:', isDevelopment ? 'development' : 'production', 'with hostname:', hostname);
  }
};

// Browser security checks
export const browserSecurityCheck = () => {
  const checks = {
    isHttps: window.location.protocol === 'https:',
    hasLocalStorage: typeof Storage !== 'undefined',
    hasSessionStorage: typeof sessionStorage !== 'undefined',
    cookiesEnabled: navigator.cookieEnabled,
    isSecureContext: window.isSecureContext,
    hasUserAgent: !!navigator.userAgent
  };
  
  const issues = [];
  
  if (!checks.isHttps && window.location.hostname !== 'localhost') {
    issues.push('Site should be served over HTTPS for security');
  }
  
  if (!checks.hasLocalStorage) {
    issues.push('Local storage is not available');
  }
  
  if (!checks.cookiesEnabled) {
    issues.push('Cookies are disabled');
  }
  
  return {
    checks,
    issues,
    isSecure: issues.length === 0
  };
};

// Form submission security
export const secureFormSubmit = async (url, data, options = {}) => {
  try {
    // Add CSRF token
    const csrfToken = csrfManager.getToken() || csrfManager.generateToken();
    
    const secureData = {
      ...data,
      _csrf: csrfToken,
      _timestamp: Date.now()
    };
    
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken,
        ...options.headers
      },
      body: JSON.stringify(secureData),
      credentials: 'same-origin',
      ...options
    };
    
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Secure form submit error:', error);
    throw error;
  }
};

// Rate limiting with exponential backoff
export const advancedRateLimit = {
  attempts: new Map(),
  
  checkLimit: (key, maxAttempts = 5, windowMs = 300000) => {
    const now = Date.now();
    const attempts = advancedRateLimit.attempts.get(key) || [];
    
    // Clean old attempts
    const validAttempts = attempts.filter(time => now - time < windowMs);
    
    if (validAttempts.length >= maxAttempts) {
      const oldestAttempt = Math.min(...validAttempts);
      const timeUntilReset = windowMs - (now - oldestAttempt);
      
      return {
        allowed: false,
        timeUntilReset,
        message: `Too many attempts. Try again in ${Math.ceil(timeUntilReset / 60000)} minutes.`
      };
    }
    
    // Add current attempt
    validAttempts.push(now);
    advancedRateLimit.attempts.set(key, validAttempts);
    
    return {
      allowed: true,
      remainingAttempts: maxAttempts - validAttempts.length
    };
  },
  
  reset: (key) => {
    advancedRateLimit.attempts.delete(key);
  }
};

// Initialize security measures
export const initializeSecurity = () => {
  // Setup CSP
  setupCSP();
  
  // Check browser security
  const securityCheck = browserSecurityCheck();
  if (!securityCheck.isSecure) {
    console.warn('Security issues detected:', securityCheck.issues);
  }
  
  // Generate initial CSRF token
  csrfManager.generateToken();
  
  // Clear old rate limiting data
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  for (const [key, attempts] of advancedRateLimit.attempts) {
    const validAttempts = attempts.filter(time => time > oneHourAgo);
    if (validAttempts.length === 0) {
      advancedRateLimit.attempts.delete(key);
    } else {
      advancedRateLimit.attempts.set(key, validAttempts);
    }
  }
  
  return {
    securityCheck,
    csrfToken: csrfManager.getToken()
  };
}; 